import { Stack } from '@mui/material';
import { noop } from 'lodash';
import { ReactNode } from 'react';

import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PresentationSlideHeader } from '@/modules/presentation/PresentationSlide/PresentationSlide';

import { SlideEditorCustomPage } from '../SlideEditor.types';
import { SlideEditorPromptButton } from '../textEditors/SlideEditor.promptButton';
import {
  SlideEditorPromptProvider,
  usePromptDisplayContent,
} from '../textEditors/SlideEditor.promptContext';
import { SlideEditorRichTextEditor } from '../textEditors/SlideEditor.richTextEditor';
import { SlideEditorTitleTextEditor } from '../textEditors/SlideEditor.titleTextEditor';
import { SlideEditorVariableButton } from '../textEditors/SlideEditor.variableButton';

export interface CustomSlideProps {
  /**
   * Page data (title, body)
   */
  page: SlideEditorCustomPage['page'];
  /**
   * Bundle-level data (header, subheader, option to include header)
   */
  bundle: SlideEditorCustomPage['bundle'];
  /**
   * Optional handler when the user changes the page name in the inline editor
   * @param newPageName updated page name
   */
  onPageNameChange?: (newPageName: string) => void;
  /**
   * Optional handler when the user changes the page content in the inline editor
   * @param newPageContent Updated page content
   * @returns
   */
  onPageContentChange?: (newPageContent: string) => void;
  /**
   * Per-keystroke variants of the change handlers above, each forwarding the
   * editor's current value (unlike the `onChange` handlers, which commit on
   * blur). The template editor uses these to track in-progress edits and arm
   * the unsaved-changes blocker before the user navigates away.
   */
  onPageNameInput?: (value: string) => void;
  onPageContentInput?: (value: string) => void;
  /**
   * Optional callback fired when the user activates either of the inline editors.
   * @returns
   */
  onClickInput?: () => void;
  /**
   * Optional handler when the user changes the AI prompt
   */
  onPromptChange?: (prompt: string | null) => void;
  /**
   * Optional handler called with the final generated content after AI
   * generation completes.  Unlike `onPageContentChange` (which may be
   * debounced for typing), this should be a non-debounced callback so the
   * generated text is persisted immediately.
   */
  onContentGenerated?: (content: string) => void;
  /** When true, `{Variable name}` tokens are highlighted with a colored
   *  background. Intended for template editing. */
  highlightVariables?: boolean;
  rightHeaderContent?: ReactNode;
}

/**
 * Standalone custom slide
 *
 * Data should be passed into this via page/bundle, with the change handles
 * optional and implementation-specific.
 */
export function CustomSlide({
  page,
  bundle,
  onPageNameChange,
  onPageContentChange,
  onPageNameInput,
  onPageContentInput,
  onClickInput,
  onPromptChange,
  onContentGenerated,
  highlightVariables,
  rightHeaderContent,
}: CustomSlideProps) {
  const { pageName, pageContent, customPrompt } = page;
  const { pageHeader, pageSubheader, includePageHeader } = bundle;

  const { displayContent, editableContent, handleRTESubmit } =
    usePromptDisplayContent({
      content: pageContent,
      customPrompt,
      onContentChange: onPageContentChange,
      onPromptChange,
    });

  let leftHeaderContent: ReactNode | undefined = undefined;

  if (includePageHeader) {
    leftHeaderContent = (
      <PresentationSlide.MainHeading
        heading={pageHeader}
        subheading={pageSubheader}
      />
    );
  }
  return (
    <Stack
      spacing={2}
      sx={{
        // this outer stack is needed to fill the height of the slide and
        // provide a relative position the middle stack can be absolutely
        // positioned against
        height: '100%',
        width: '100%',
        position: 'relative',
        pointerEvents: 'auto',
      }}
    >
      <Stack
        sx={{
          // this middle stack provides a "fixed" height (in flexbox terms)
          // where overflowing content can be hidden
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'clip', // use clip because we don't want the editor to scroll within
        }}
      >
        <Stack
          sx={{
            // Don't use the built-in header:
            // - built-in header doesn't have a fixed height
            // - body of this slide needs to sit at a fixed Y so it
            //   matches every other E360 refresh slide (T1-6110)
            // Why do this?
            // - Create a stacking context exactly this tall
            // - If the header is needed, put it in the stack
            // - With no header, the title sits 60px from the slide top
            //   (matches `PresentationSlideRefreshLayout`'s spacer)
            flex: `0 0 ${includePageHeader ? '204px' : '60px'}`,
            px: 2,
          }}
        >
          {includePageHeader && (
            <PresentationSlideHeader
              leftHeaderContent={leftHeaderContent}
              rightHeaderContent={rightHeaderContent}
            />
          )}
        </Stack>
        <Stack
          sx={{ px: 10.5, flex: '1 1 auto', position: 'relative' }}
          spacing={4}
        >
          <SlideEditorTitleTextEditor
            title={pageName}
            onChange={onPageNameChange}
            onInput={onPageNameInput}
            onClickInput={onClickInput}
            highlightVariables={highlightVariables}
            // Custom slides are content surfaces — match the
            // refresh-bundle title treatment (Romie Medium 500) instead
            // of the cover-slide hero Bold (700).
            variant="content"
          />
          <Stack
            data-testid="CustomSlide-body"
            sx={{
              // this inner stack won't fill past the middle stack, and handles
              // laying out the content
              justifyContent: 'flex-start',
              flex: '1 1 100%',
            }}
          >
            <SlideEditorPromptProvider
              customPrompt={customPrompt ?? null}
              pageContent={pageContent}
              onPromptChange={onPromptChange ?? noop}
              onContentGenerated={onContentGenerated ?? onPageContentChange}
            >
              <SlideEditorRichTextEditor
                content={displayContent}
                editableContent={editableContent}
                onSubmit={handleRTESubmit}
                onRawUpdate={onPageContentInput}
                onClick={onClickInput}
                highlightVariables={highlightVariables}
                additionalToolbarButtons={
                  <>
                    <SlideEditorPromptButton />
                    <SlideEditorVariableButton />
                  </>
                }
              />
            </SlideEditorPromptProvider>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
